/**
 * Detects a failed lazy-chunk load (webpack `ChunkLoadError`).
 *
 * Used by the app-level and section-level error boundaries to distinguish a
 * transient chunk-load failure (worth a cache-busting `?retry=N` reload) from a
 * genuine render error (which a reload won't fix).
 *
 * Matches:
 *  - `error.name === 'ChunkLoadError'` (canonical webpack name)
 *  - `"Loading chunk … failed"` (JS chunks — name sometimes lost across async boundaries)
 *  - `"Loading CSS chunk … failed"` (CSS chunks via mini-css-extract-plugin, which throws
 *     a plain `Error` instead of `ChunkLoadError`)
 *
 * ⚠️  `loadableFromNamedExport.ts` has its own separate detector — do NOT widen that one
 *     without also making its retry path CSS-aware (it injects a `<script>`, not a `<link>`).
 *
 * @param error The caught error (nullable — boundaries hold `Error | null` state).
 * @returns `true` if the error looks like a chunk-load failure.
 */
export const isChunkLoadError = (error: Error | null): boolean => {
  if (!error) return false;
  return error.name === 'ChunkLoadError' || /loading (?:css )?chunk [\w./~-]+ failed\b/i.test(error.message ?? '');
};
